import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
// UI状态相关atoms
// 侧边栏状态
export const sidebarOpenAtom = atomWithStorage('sidebar_open', true);
export const sidebarActiveTabAtom = atomWithStorage('sidebar_active_tab', 'scripts');
// 模态框状态
export const modalOpenAtom = atom(false);
export const modalTypeAtom = atom(null);
export const modalDataAtom = atom(null);
// 模态框组合状态
export const modalStateAtom = atom((get) => ({
    isOpen: get(modalOpenAtom),
    type: get(modalTypeAtom),
    data: get(modalDataAtom),
}));
// 通知系统
export const notificationsAtom = atom([]);
// 加载状态管理
export const loadingStatesAtom = atom({});
// 主题设置
export const themeAtom = atomWithStorage('theme', 'dark');
// 语言设置
export const languageAtom = atomWithStorage('language', 'en');
// 用户偏好设置
export const userPreferencesAtom = atomWithStorage('user_preferences', {
    showWelcomeScreen: true,
    enableNotifications: true,
    enableSoundEffects: false,
    autoSaveEnabled: true,
    autoSaveInterval: 5,
    fontSize: 'medium',
    reducedMotion: false,
});
// 应用配置状态
export const appConfigAtom = atomWithStorage('app_config', {
    version: '0.1.0',
    firstRun: true,
    telemetryEnabled: false,
    updateChannel: 'stable',
});
// 当前输入框内容
export const messageInputAtom = atom('');
// 当前是否正在发送消息
export const isSendingMessageAtom = atom(false);
// 当前是否正在生成AI响应
export const isGeneratingResponseAtom = atom(false);
// 当前选择的角色ID（用于角色编辑器）
export const selectedCharacterIdAtom = atom(null);
// 脚本编辑器状态
export const scriptEditorAtom = atom({
    isEditing: false,
    hasUnsavedChanges: false,
    currentScriptId: null,
});
// 窗口尺寸状态
export const windowSizeAtom = atom({
    width: 1024,
    height: 768,
    isMaximized: false,
    isFullscreen: false,
});
// 错误状态
export const errorStateAtom = atom({
    hasError: false,
    error: null,
    errorBoundaryInfo: null,
});
// 搜索状态（全局搜索）
export const globalSearchAtom = atom({
    query: '',
    isOpen: false,
    results: [],
    isSearching: false,
});
// 移动端主导航页：'home' | 'chat'
export const activePageAtom = atomWithStorage('active_page', 'home');
// 首页当前类目（服务端返回ID或 'mine'）
export const homeActiveCategoryAtom = atomWithStorage('home_active_category', null);
// 聊天覆盖层（全屏沉浸式）
export const chatOverlayOpenAtom = atom(false);
export const chatOverlayReturnAtom = atom('home');
// 本地聊天模式（MineGame），不依赖服务器WS连接
export const localChatModeAtom = atom(false);
// 当前正在编辑的 MineGame 条目 ID（用于创建/编辑页）
export const editingMineIdAtom = atom(null);
export const createOverlayOpenAtom = atom(false);
// UI Designer overlay (game UI design preview)
export const uiDesignerOpenAtom = atom(false);
export const uiDesignerCodeAtom = atom('');
// 聊天记录页聚焦的脚本ID（用于展开某个游戏的存档树）
export const chatHistoryFocusScriptIdAtom = atom(null);
// 新首页：落地图与应用列表
export const homeShowGamesAtom = atomWithStorage('home_show_games', false);
export const homeBackgroundImageAtom = atomWithStorage('home_background_image', null);
export const homeTimeImageAtom = atomWithStorage('home_time_image', null);
// UI操作相关的写入atoms
// 切换侧边栏
export const toggleSidebarAtom = atom(null, (get, set) => {
    const isOpen = get(sidebarOpenAtom);
    set(sidebarOpenAtom, !isOpen);
});
// 打开模态框
export const openModalAtom = atom(null, (get, set, { type, data }) => {
    set(modalTypeAtom, type);
    set(modalDataAtom, data);
    set(modalOpenAtom, true);
});
// 关闭模态框
export const closeModalAtom = atom(null, (get, set) => {
    set(modalOpenAtom, false);
    set(modalTypeAtom, null);
    set(modalDataAtom, null);
});
// 添加通知
export const addNotificationAtom = atom(null, (get, set, notification) => {
    const notifications = get(notificationsAtom);
    const newNotification = {
        ...notification,
        id: `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
    };
    set(notificationsAtom, [newNotification, ...notifications]);
    // 自动移除通知（如果设置了duration）
    if (notification.duration && notification.duration > 0) {
        setTimeout(() => {
            set(removeNotificationAtom, newNotification.id);
        }, notification.duration);
    }
});
// 移除通知
export const removeNotificationAtom = atom(null, (get, set, notificationId) => {
    const notifications = get(notificationsAtom);
    set(notificationsAtom, notifications.filter(n => n.id !== notificationId));
});
// 清除所有通知
export const clearNotificationsAtom = atom(null, (get, set) => {
    set(notificationsAtom, []);
});
// 设置加载状态
export const setLoadingAtom = atom(null, (get, set, { key, loading }) => {
    const loadingStates = get(loadingStatesAtom);
    set(loadingStatesAtom, {
        ...loadingStates,
        [key]: loading,
    });
});
// 检查是否正在加载
export const isLoadingAtom = atom((get) => {
    const loadingStates = get(loadingStatesAtom);
    return (key) => loadingStates[key] || false;
});
// 检查是否有任何加载状态
export const hasAnyLoadingAtom = atom((get) => {
    const loadingStates = get(loadingStatesAtom);
    return Object.values(loadingStates).some(Boolean);
});
// 设置错误状态
export const setErrorAtom = atom(null, (get, set, error, errorBoundaryInfo) => {
    set(errorStateAtom, {
        hasError: !!error,
        error,
        errorBoundaryInfo: errorBoundaryInfo || null,
    });
});
// 清除错误状态
export const clearErrorAtom = atom(null, (get, set) => {
    set(errorStateAtom, {
        hasError: false,
        error: null,
        errorBoundaryInfo: null,
    });
});
// 更新用户偏好
export const updateUserPreferencesAtom = atom(null, (get, set, updates) => {
    const current = get(userPreferencesAtom);
    set(userPreferencesAtom, { ...current, ...updates });
});
// 开始/停止消息发送状态
export const startSendingMessageAtom = atom(null, (get, set) => {
    set(isSendingMessageAtom, true);
    set(isGeneratingResponseAtom, false);
});
export const startGeneratingResponseAtom = atom(null, (get, set) => {
    set(isSendingMessageAtom, false);
    set(isGeneratingResponseAtom, true);
});
export const stopMessageProcessingAtom = atom(null, (get, set) => {
    set(isSendingMessageAtom, false);
    set(isGeneratingResponseAtom, false);
});
// 全局搜索操作
export const openGlobalSearchAtom = atom(null, (get, set) => {
    const search = get(globalSearchAtom);
    set(globalSearchAtom, { ...search, isOpen: true });
});
export const closeGlobalSearchAtom = atom(null, (get, set) => {
    const search = get(globalSearchAtom);
    set(globalSearchAtom, {
        ...search,
        isOpen: false,
        query: '',
        results: [],
        isSearching: false,
    });
});
export const setGlobalSearchQueryAtom = atom(null, (get, set, query) => {
    const search = get(globalSearchAtom);
    set(globalSearchAtom, { ...search, query });
});
